import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, LightbulbIcon, ArrowRightIcon, AlertCircleIcon } from 'lucide-react';
interface Supplement {
  name: string;
  dosage: string;
  timing: 'Morning' | 'Pre-Workout' | 'Post-Workout' | 'Evening';
  description: string;
}
interface GoalStack {
  id: string;
  title: string;
  emoji: string;
  supplements: Supplement[];
  proTip: string;
}
const stacks: GoalStack[] = [{
  id: 'muscle',
  title: 'Muscle Building',
  emoji: '💪',
  proTip: 'Take creatine consistently every day, even on rest days, to maintain muscle saturation levels.',
  supplements: [{
    name: 'Whey Protein Isolate',
    dosage: '1-2 scoops (25-50g)',
    timing: 'Post-Workout',
    description: 'Fast-absorbing protein to kickstart muscle protein synthesis and recovery.'
  }, {
    name: 'Creatine Monohydrate',
    dosage: '5g',
    timing: 'Pre-Workout',
    description: 'Increases ATP production for more power and strength during heavy lifts.'
  }, {
    name: 'BCAAs',
    dosage: '5-10g',
    timing: 'Morning',
    description: 'Prevents muscle breakdown during fasted states or intense training.'
  }, {
    name: 'Casein Protein',
    dosage: '1 scoop (25g)',
    timing: 'Evening',
    description: 'Slow-digesting protein to feed muscles overnight during sleep.'
  }]
}, {
  id: 'fat-loss',
  title: 'Fat Loss',
  emoji: '🔥',
  proTip: 'Combine thermogenics with fasted cardio for optimal fat oxidation, but stay hydrated!',
  supplements: [{
    name: 'Thermogenic Fat Burner',
    dosage: '1 capsule',
    timing: 'Morning',
    description: 'Boosts metabolism and increases core temperature to burn more calories.'
  }, {
    name: 'L-Carnitine',
    dosage: '1500-3000mg',
    timing: 'Pre-Workout',
    description: 'Helps transport fatty acids into mitochondria to be burned as energy.'
  }, {
    name: 'Whey Protein Isolate',
    dosage: '1 scoop (25g)',
    timing: 'Post-Workout',
    description: 'Maintains lean muscle mass while in a caloric deficit.'
  }]
}, {
  id: 'endurance',
  title: 'Endurance & Stamina',
  emoji: '🏃',
  proTip: 'Carb loading is key. Mix your electrolytes with a fast-digesting carb source during long sessions.',
  supplements: [{
    name: 'Beta-Alanine',
    dosage: '3-5g',
    timing: 'Pre-Workout',
    description: 'Buffers lactic acid to delay muscle fatigue during high-intensity exercise.'
  }, {
    name: 'Electrolyte Complex',
    dosage: '1 serving',
    timing: 'Morning',
    description: 'Maintains hydration and prevents cramping during prolonged sweating.'
  }, {
    name: 'Citrulline Malate',
    dosage: '6-8g',
    timing: 'Pre-Workout',
    description: 'Increases nitric oxide production for better blood flow and oxygen delivery.'
  }]
}, {
  id: 'recovery',
  title: 'Recovery & Repair',
  emoji: '🧘',
  proTip: 'Sleep is your #1 recovery tool. Take ZMA 30-60 minutes before bed on an empty stomach.',
  supplements: [{
    name: 'L-Glutamine',
    dosage: '5-10g',
    timing: 'Post-Workout',
    description: 'Supports immune function and gut health, crucial for intense training recovery.'
  }, {
    name: 'ZMA (Zinc, Magnesium, B6)',
    dosage: '3 capsules',
    timing: 'Evening',
    description: 'Improves sleep quality and supports natural hormone production.'
  }, {
    name: 'Omega-3 Fish Oil',
    dosage: '2-3g EPA/DHA',
    timing: 'Morning',
    description: 'Reduces exercise-induced inflammation and joint soreness.'
  }]
}, {
  id: 'health',
  title: 'General Health',
  emoji: '❤️',
  proTip: 'Consistency is more important than perfection. Take your daily vitamins with your first meal.',
  supplements: [{
    name: 'High-Quality Multivitamin',
    dosage: '1 serving',
    timing: 'Morning',
    description: 'Fills nutritional gaps in your daily diet.'
  }, {
    name: 'Vitamin D3 + K2',
    dosage: '2000-5000 IU',
    timing: 'Morning',
    description: 'Supports bone health, immune system, and mood regulation.'
  }, {
    name: 'Probiotics',
    dosage: '10-50 Billion CFU',
    timing: 'Evening',
    description: 'Promotes a healthy gut microbiome for better nutrient absorption.'
  }]
}];
const getTimingColor = (timing: Supplement['timing']) => {
  switch (timing) {
    case 'Morning':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'Pre-Workout':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Post-Workout':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Evening':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};
export function StackingGuidePage() {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string>('muscle');
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <HelmetProvider data-id="element-9044" data-mp="25fd5quvvy5u2">
      <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-9045" data-mp="25fwebkznbhvf">
        <SEO title="Supplement Stacking Guide | Innova Pharmacy" description="Learn how to combine supplements effectively for your specific fitness goals." data-id="element-9046" data-mp="1rfljy7z4nl5i" />

        <div className="hidden md:block" data-id="element-9047" data-mp="g18baacy3ca2">
          <AnnouncementBar data-id="element-9048" data-mp="g06oj8mkh1ci" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-9049" data-mp="l0kyiw7qald6" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-9050" data-mp="chzgq2vg9qsl" />
        </div>

        <div className="fixed inset-x-0 top-0 z-50 md:hidden" data-id="element-9051" data-mp="hiwxqc4q4esi">
          <MobileInternalHeader title="Stacking Guide" data-id="element-9052" data-mp="hgkfryyyu4fg" />
        </div>

        <div className="pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-9053" data-mp="3pz8jgd3p3etm">
          <PageHeroBreadcrumb title="Stacking Guide" description="Learn how to combine supplements effectively for your specific fitness goals." breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Stacking Guide'
        }]} data-id="element-9054" data-mp="3kzvv1nx6dzox" />
        </div>

        <main className="flex-grow container mx-auto px-4 py-8 max-w-3xl" data-id="element-9055" data-mp="329e33lk6384m">
          <div className="text-center mb-8" data-id="element-9056" data-mp="2gpktt1256sb3">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-9057" data-mp="2gqut1tkqe6jb">
              Supplement Stacks by Goal
            </h2>
            <p className="text-gray-600" data-id="element-9058" data-mp="2lqqov5j9yrns">
              Discover the perfect combination of supplements tailored to your
              specific fitness objectives. Expand a goal below to see the
              recommended stack, dosages, and timing.
            </p>
          </div>

          <div className="space-y-4 mb-12" data-id="element-9059" data-mp="2zo5fgdl9l5hc">
            {stacks.map(stack => <div key={stack.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-9060" data-mp="34m789q8hsj60">
                <button onClick={() => setExpandedId(expandedId === stack.id ? '' : stack.id)} className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors" data-id="element-9061" data-mp="3eduwli9r5k39">
                  <div className="flex items-center gap-4" data-id="element-9062" data-mp="1bqedemqgll1v">
                    <span className="text-3xl" data-id="element-9063" data-mp="1bojt9an0hwyu">{stack.emoji}</span>
                    <div className="text-left" data-id="element-9064" data-mp="17o00skddvhj1">
                      <h3 className="font-bold text-lg text-gray-900" data-id="element-9065" data-mp="17rfb79w442e4">
                        {stack.title}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-9066" data-mp="xroldlihybkd">
                        {stack.supplements.length} Supplements
                      </p>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: expandedId === stack.id ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-9067" data-mp="1qfan0o85z64l">
                    <ChevronDownIcon className="w-6 h-6 text-gray-400" data-id="element-9068" data-mp="1gnwoaio1eaci" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-9069" data-mp="29dwqg9r5l9bo">
                  {expandedId === stack.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-9070" data-mp="ztv4z6p2nhxc">
                      <div className="px-6 pb-6 pt-2 border-t border-gray-100" data-id="element-9071" data-mp="1sj2bosyojdto">
                        {/* Pro Tip */}
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex gap-3" data-id="element-9072" data-mp="1nlablbnvslee">
                          <LightbulbIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9073" data-mp="21l1thnhiqlxo" />
                          <div data-id="element-9074" data-mp="1xjgffyt901lw">
                            <h4 className="font-semibold text-blue-900 mb-1" data-id="element-9075" data-mp="1xgc148m2jphi">
                              Pro Tip
                            </h4>
                            <p className="text-sm text-blue-800" data-id="element-9076" data-mp="2l623m29w3dnc">
                              {stack.proTip}
                            </p>
                          </div>
                        </div>

                        {/* Supplements List */}
                        <div className="space-y-4 mb-6" data-id="element-9077" data-mp="2c78bao3ld9fl">
                          {stack.supplements.map((supp, index) => <div key={index} className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100" data-id="element-9078" data-mp="226o7peslnsu7" data-mpi={index}>
                              <div className="flex-grow" data-id="element-9079" data-mp="1si68h1zyxtso">
                                <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-9080" data-mp="1ehdc0bq3womx">
                                  <h4 className="font-bold text-gray-900" data-id="element-9081" data-mp="1ejpuqr3ov311">
                                    {supp.name}
                                  </h4>
                                  <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${getTimingColor(supp.timing)}`} data-id="element-9082" data-mp="3l6fat0dbjg27">
                                    {supp.timing}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2" data-id="element-9083" data-mp="37cedmb20m13y">
                                  {supp.description}
                                </p>
                                <div className="text-sm font-medium text-gray-900" data-id="element-9084" data-mp="3y0e9d6qqc00">
                                  Dosage:{' '}
                                  <span className="text-gray-600 font-normal" data-id="element-9085" data-mp="3v5mr9wfcnqtj">
                                    {supp.dosage}
                                  </span>
                                </div>
                              </div>
                            </div>)}
                        </div>

                        {/* Shop Button */}
                        <button onClick={() => navigate('/categories')} className="w-full bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition-colors flex items-center justify-center" data-id="element-9086" data-mp="24ng3tmbjl3bc">
                          Shop This Stack
                          <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-9087" data-mp="2nc4k1uz2ykgs" />
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-100 rounded-xl p-4 flex gap-3 text-gray-600 text-sm" data-id="element-9088" data-mp="2x8fxwpebjidc">
            <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-9089" data-mp="327aquk602nb6" />
            <p data-id="element-9090" data-mp="12xu7wplun868">
              <strong data-id="element-9091" data-mp="12ycdwdybd0rc">Disclaimer:</strong> This stacking guide is for
              educational purposes only. Always consult with a healthcare
              professional before starting any new supplement regimen,
              especially if you have pre-existing medical conditions or are
              taking medications.
            </p>
          </div>
        </main>

        <Footer data-id="element-9092" data-mp="37buulj3wzqi0" />
        <MobileFooterNav data-id="element-9093" data-mp="37cmhm5jg4fwo" />
        <PharmacistChat data-id="element-9094" data-mp="37as4hdef1s35" />

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={loc => {
        setSelectedLocation(loc);
        setLocationModalOpen(false);
      }} data-id="element-9095" data-mp="32djmpdx6jq2f" />
      </div>
    </HelmetProvider>;
}